import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api, formatKES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { CheckCircle2, Clock, FileSignature, Scale, ArrowLeft, Download } from "lucide-react";
import { toast } from "sonner";
import EscrowPanel from "../components/EscrowPanel";

export default function ContractDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [features, setFeatures] = useState({});
  const [signName, setSignName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = () => api.get(`/contracts/${id}`).then((r) => setC(r.data));
  useEffect(() => {
    load();
    api.get("/config/features").then((r) => setFeatures(r.data)).catch(() => {});
  }, [id]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get(`/contracts/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KSB-Contract-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!c) return <div className="p-20 text-center text-[#5B6478]">Loading...</div>;

  const isMyParty =
    (user?.role === "customer" && c.customer_id === user.id) ||
    (user?.role === "employee" && c.employee_user_id === user.id);
  const alreadySigned =
    (user?.role === "customer" && c.customer_signed) ||
    (user?.role === "employee" && c.employee_signed);

  const sign = async () => {
    if (!signName.trim()) return toast.error("Type your full legal name");
    if (!agree) return toast.error("You must confirm agreement");
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${id}/sign`, {
        signature_full_name: signName,
        agreed: true,
      });
      setC(data);
      toast.success("Signature captured. Legally binding under Kenya's Electronic Transactions Act.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to sign");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FE]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <button onClick={() => nav(-1)} data-testid="contract-back" className="flex items-center gap-2 text-sm text-[#5B6478] hover:text-[#1E3A8A] mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-white border border-[#E4E7F5] rounded-md p-8">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="label-eyebrow mb-2 flex items-center gap-1"><Scale className="w-3 h-3" /> Employment Contract</div>
              <h1 className="font-display text-3xl font-bold tracking-tight">{c.role_title}</h1>
              <p className="text-[#5B6478] mt-2 text-sm">
                Between <span className="font-semibold">{c.customer_name}</span> (Employer) and <span className="font-semibold">{c.employee_name}</span> (Staff)
              </p>
            </div>
            <Badge className={`capitalize ${
              c.status === "active" ? "bg-[#1E3A8A]"
              : c.status === "terminated" ? "bg-[#B91C1C]"
              : c.status === "completed" ? "bg-emerald-600 hover:bg-emerald-600"
              : "bg-amber-100 text-amber-900 hover:bg-amber-100"
            }`}>
              {c.status?.replace("_", " ")}
            </Badge>
          </div>

          <div className="mt-4 flex justify-end">
            <Button
              data-testid="download-pdf-btn"
              onClick={downloadPdf}
              disabled={downloading}
              variant="outline"
              className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-[#F8F9FE]"
            >
              <Download className="w-4 h-4 mr-2" /> {downloading ? "Preparing..." : "Download PDF"}
            </Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 pt-6 border-t border-[#E4E7F5]">
            <TermItem label="Salary" value={`${formatKES(c.salary_agreed)} / ${c.pay_frequency}`} />
            <TermItem label="Start date" value={c.start_date} />
            <TermItem label="Working hours" value={c.working_hours} />
            <TermItem label="Notice period" value={`${c.notice_period_days} days`} />
            <TermItem label="Probation" value={`${c.probation_days} days`} />
            <TermItem label="End date" value={c.end_date || "Open-ended"} />
            <TermItem label="Category" value={c.category?.replace("-", " ")} />
            <TermItem label="Drafted" value={new Date(c.created_at).toLocaleDateString()} />
          </div>

          {c.additional_terms && (
            <div className="mt-6 bg-[#EEF0FA] rounded-md p-4">
              <div className="label-eyebrow mb-2">Additional terms</div>
              <div className="text-sm">{c.additional_terms}</div>
            </div>
          )}

          <div className="mt-8">
            <div className="label-eyebrow mb-3">Legal clauses (Republic of Kenya)</div>
            <ol className="space-y-2 text-sm text-[#0F1E4B] list-decimal pl-5">
              {c.kenya_clauses?.map((k, i) => <li key={i} className="leading-relaxed">{k}</li>)}
            </ol>
          </div>

          {c.terms_and_conditions && (
            <div className="mt-8">
              <div className="label-eyebrow mb-3">Terms &amp; Conditions</div>
              <div className="grid md:grid-cols-3 gap-4">
                {c.terms_and_conditions.mutual?.length > 0 && (
                  <div className="bg-[#EEF0FA] border border-[#E4E7F5] rounded-md p-4">
                    <div className="font-display font-bold text-sm text-[#7C3AED] mb-2 uppercase tracking-wider">Mutual</div>
                    <ul className="space-y-2 text-xs text-[#0F1E4B] list-disc pl-4">
                      {c.terms_and_conditions.mutual.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
                    </ul>
                  </div>
                )}
                {c.terms_and_conditions.employer?.length > 0 && (
                  <div className="bg-white border border-[#E4E7F5] rounded-md p-4">
                    <div className="font-display font-bold text-sm text-[#1E3A8A] mb-2 uppercase tracking-wider">Employer obligations</div>
                    <ul className="space-y-2 text-xs text-[#0F1E4B] list-disc pl-4">
                      {c.terms_and_conditions.employer.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
                    </ul>
                  </div>
                )}
                {c.terms_and_conditions.staff?.length > 0 && (
                  <div className="bg-white border border-[#E4E7F5] rounded-md p-4">
                    <div className="font-display font-bold text-sm text-[#1E3A8A] mb-2 uppercase tracking-wider">Staff obligations</div>
                    <ul className="space-y-2 text-xs text-[#0F1E4B] list-disc pl-4">
                      {c.terms_and_conditions.staff.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <p className="text-xs text-[#5B6478] mt-3">
                By signing this contract, both the Employer and the Staff Member confirm they have read and agree to all mutual, employer, and staff Terms &amp; Conditions above.
              </p>
            </div>
          )}

          {/* Monthly Upfront Escrow + KSB Guarantee */}
          {c.status !== "pending_signatures" && (
            <EscrowPanel contract={c} currentUser={user} features={features} onContractUpdate={load} />
          )}

          {/* SIGNATURES */}
          <div className="mt-10 pt-8 border-t border-[#E4E7F5] grid md:grid-cols-2 gap-6">
            <SignatureBlock
              label="Employer"
              name={c.customer_name}
              signed={c.customer_signed}
              signature={c.customer_signature}
              at={c.customer_signed_at}
            />
            <SignatureBlock
              label="Staff"
              name={c.employee_name}
              signed={c.employee_signed}
              signature={c.employee_signature}
              at={c.employee_signed_at}
            />
          </div>

          {/* SIGN FORM */}
          {isMyParty && !alreadySigned && c.status === "pending_signatures" && (
            <div className="mt-8 pt-8 border-t border-[#E4E7F5] bg-[#EEF0FA] -mx-8 -mb-8 p-8 rounded-b-md">
              <div className="font-display text-xl font-bold flex items-center gap-2">
                <FileSignature className="w-5 h-5" /> Sign this contract
              </div>
              <p className="text-sm text-[#5B6478] mt-2 max-w-xl">
                By typing your full legal name below and confirming, you sign this contract electronically. Under the Kenya Information & Communications Act (Electronic Transactions), this signature is legally binding.
              </p>
              <div className="mt-5 grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="label-eyebrow">Your full legal name</Label>
                  <Input data-testid="sign-name" value={signName} onChange={(e) => setSignName(e.target.value)} className="mt-2 border-[#E4E7F5] bg-white" placeholder="e.g. Wanjiru Kamau" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-start gap-2 text-sm">
                    <input data-testid="sign-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
                    <span>I have read and agree to all terms, including the Kenya legal clauses.</span>
                  </label>
                </div>
              </div>
              <Button
                data-testid="sign-submit"
                onClick={sign}
                disabled={busy}
                className="mt-6 bg-[#1E3A8A] hover:bg-[#2E4FB8] text-[#F8F9FE]"
              >
                <FileSignature className="w-4 h-4 mr-2" /> Sign electronically
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TermItem({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow text-[10px]">{label}</div>
      <div className="font-display font-semibold capitalize mt-1">{value || "—"}</div>
    </div>
  );
}

function SignatureBlock({ label, name, signed, signature, at }) {
  return (
    <div className="border border-[#E4E7F5] rounded-md p-5">
      <div className="label-eyebrow flex items-center gap-1">
        {signed ? <CheckCircle2 className="w-3 h-3 text-[#1E3A8A]" /> : <Clock className="w-3 h-3 text-amber-600" />}
        {label}
      </div>
      <div className="font-display font-semibold mt-2">{name}</div>
      {signed ? (
        <>
          <div className="mt-4 font-display italic text-2xl text-[#1E3A8A] border-b border-[#E4E7F5] pb-2">{signature}</div>
          <div className="text-xs text-[#5B6478] mt-2">Signed on {new Date(at).toLocaleString()}</div>
        </>
      ) : (
        <div className="mt-4 text-sm text-[#5B6478] italic">Awaiting signature</div>
      )}
    </div>
  );
}
